import { useQuery } from '@tanstack/react-query';
import type { BranchDto, DayBookDay, DayBookEntry, DayBookReport, ReportExportRequest } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { Badge } from '@/components/Badge';
import { KpiCard } from '@/components/KpiCard';
import { ExportMenu } from '@/components/ExportMenu';
import { dayBookKeys, fetchDayBook } from '@/features/reports/day-book-api';
import { downloadReportExport } from '@/features/reports/api';
import type { ExportFormat } from '@/features/reports/api';
import { buildExportTable, exportSubtitle } from '@/features/reports/export-table';
import type { ExportColumnSpec } from '@/features/reports/export-table';
import type { ReportFilterRow } from '@/features/reports/reportFilters';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);
const round2 = (value: number) => Math.round(value * 100) / 100;

function formatDate(value: string): string {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

/* ───────────────────────────── Day totals ───────────────────────────── */

const DAY_COLUMNS: Column<DayBookDay>[] = [
  { key: 'date', header: 'Date', cell: (row) => formatDate(row.date) },
  {
    key: 'count',
    header: 'Lines',
    align: 'right',
    cell: (row) => String(row.moneyIn.length + row.moneyOut.length),
  },
  { key: 'totalIn', header: 'Money in', align: 'right', cell: (row) => money(row.totalIn) },
  { key: 'totalOut', header: 'Money out', align: 'right', cell: (row) => money(row.totalOut) },
  {
    key: 'net',
    header: 'Net',
    align: 'right',
    cell: (row) => <span className={row.net < 0 ? 'text-rose-700' : undefined}>{money(row.net)}</span>,
  },
];

/** Re-total a day from the lines the toolbar left, so a search shows that search's money. */
function summariseDays(lines: DayBookEntry[]): DayBookDay[] {
  const byDate = new Map<string, DayBookDay>();
  for (const line of lines) {
    let day = byDate.get(line.date);
    if (!day) {
      day = { date: line.date, moneyIn: [], moneyOut: [], totalIn: 0, totalOut: 0, net: 0 };
      byDate.set(line.date, day);
    }
    if (line.direction === 'in') {
      day.moneyIn.push(line);
      day.totalIn = round2(day.totalIn + line.amount);
    } else {
      day.moneyOut.push(line);
      day.totalOut = round2(day.totalOut + line.amount);
    }
  }
  const days = [...byDate.values()];
  for (const day of days) day.net = round2(day.totalIn - day.totalOut);
  return days;
}

/* ───────────────────────────── Journal ───────────────────────────── */

const FILTER_FIELDS = ['Type', 'Method', 'Party', 'Direction'] as const;

const TOGGLEABLE = ['time', 'label', 'reference', 'against', 'party', 'method', 'detail', 'moneyIn', 'moneyOut'] as const;

const COLUMN_LABELS: Record<string, string> = {
  time: 'Time',
  label: 'Type',
  reference: 'Reference',
  against: 'Against',
  party: 'Party',
  method: 'Method',
  detail: 'Detail',
  moneyIn: 'In',
  moneyOut: 'Out',
};

const JOURNAL_COLUMNS: Column<DayBookEntry>[] = [
  { key: 'date', header: 'Date', pinned: true, cell: (row) => formatDate(row.date) },
  { key: 'time', header: 'Time', cell: (row) => row.time || '—' },
  {
    key: 'label',
    header: 'Type',
    // Money in green, money out red — the POS Day Book's own colouring.
    cell: (row) => (
      <Badge tone={row.direction === 'in' ? 'success' : 'danger'} dot>
        {row.label}
      </Badge>
    ),
  },
  { key: 'reference', header: 'Reference', cell: (row) => row.reference || '—' },
  { key: 'against', header: 'Against', visibility: 'hidden sm:table-cell', cell: (row) => row.against || '—' },
  { key: 'party', header: 'Party', cell: (row) => row.party || '—' },
  { key: 'method', header: 'Method', cell: (row) => row.method || '—' },
  { key: 'detail', header: 'Detail', visibility: 'hidden sm:table-cell', cell: (row) => row.detail || '—' },
  {
    key: 'moneyIn',
    header: 'In',
    align: 'right',
    cell: (row) => (row.direction === 'in' ? money(row.amount) : ''),
  },
  {
    key: 'moneyOut',
    header: 'Out',
    align: 'right',
    cell: (row) => (row.direction === 'out' ? money(row.amount) : ''),
  },
];

function fieldValue(row: DayBookEntry, field: string): string | undefined {
  switch (field) {
    case 'Type':
      return row.label;
    case 'Method':
      return row.method;
    case 'Party':
      return row.party;
    case 'Direction':
      return row.direction === 'in' ? 'In' : 'Out';
    default:
      return undefined;
  }
}

/** Each day's money in then money out, in the order the server gave them. */
function journalOf(days: DayBookDay[]): DayBookEntry[] {
  return days.flatMap((day) =>
    [...day.moneyIn, ...day.moneyOut].sort((a, b) => a.time.localeCompare(b.time)),
  );
}

interface DayBookView {
  lines: DayBookEntry[];
  days: DayBookDay[];
  totalIn: number;
  totalOut: number;
  net: number;
}

/**
 * The endpoint returns the whole range, so the toolbar narrows it here — and
 * the totals follow, so a search for one supplier shows that supplier's money
 * rather than the whole range's. Shared by the screen and the export.
 */
function narrowDayBook(
  report: DayBookReport,
  toolbar: { search: string; filterRows: ReportFilterRow[]; matchAny: boolean },
): DayBookView {
  const all = journalOf(report.days);
  const lines = applyClientFilters(all, {
    search: toolbar.search,
    filterRows: toolbar.filterRows,
    matchAny: toolbar.matchAny,
    fieldValue,
    searchValues: (row) => [row.label, row.reference, row.against, row.party, row.method, row.detail],
  });
  const narrowed = lines.length !== all.length;
  const days = narrowed ? summariseDays(lines) : report.days;
  const totalIn = narrowed ? round2(days.reduce((sum, day) => sum + day.totalIn, 0)) : report.totalIn;
  const totalOut = narrowed ? round2(days.reduce((sum, day) => sum + day.totalOut, 0)) : report.totalOut;
  return { lines, days, totalIn, totalOut, net: round2(totalIn - totalOut) };
}

/** Plain-text twins of JOURNAL_COLUMNS, keyed the same so the Columns layout applies. */
const JOURNAL_EXPORT: ExportColumnSpec<DayBookEntry>[] = [
  { key: 'date', header: 'Date', text: (row) => formatDate(row.date) },
  { key: 'time', header: 'Time', text: (row) => row.time || '—' },
  { key: 'label', header: 'Type', text: (row) => row.label },
  { key: 'reference', header: 'Reference', text: (row) => row.reference || '—' },
  { key: 'against', header: 'Against', text: (row) => row.against || '—' },
  { key: 'party', header: 'Party', text: (row) => row.party || '—' },
  { key: 'method', header: 'Method', text: (row) => row.method || '—' },
  { key: 'detail', header: 'Detail', width: 2, text: (row) => row.detail || '—' },
  { key: 'moneyIn', header: 'In', align: 'right', money: true, text: (row) => (row.direction === 'in' ? money(row.amount) : '') },
  { key: 'moneyOut', header: 'Out', align: 'right', money: true, text: (row) => (row.direction === 'out' ? money(row.amount) : '') },
];

/** The "Every line" table as shown, with the period's net under it. */
function dayBookTable(
  report: DayBookReport,
  view: DayBookView,
  visibleKeys: readonly string[],
  branch: { branchId: string; branches: readonly BranchDto[] },
): ReportExportRequest {
  return buildExportTable({
    title: 'Day book',
    subtitle: exportSubtitle(report, branch),
    from: report.from,
    to: report.to,
    columns: JOURNAL_EXPORT,
    visibleKeys,
    rows: view.lines,
    totals: { label: 'Total', cells: { moneyIn: money(view.totalIn), moneyOut: money(view.totalOut) } },
    notes: [`Net (money in − money out): ${money(view.net)}`],
  });
}

export function DayBookPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);
  const layout = useReportLayout('day-book', TOGGLEABLE);
  const columns = applyReportLayout(JOURNAL_COLUMNS, layout);

  const query = useQuery({
    queryKey: dayBookKeys.report(filters),
    queryFn: ({ signal }) => fetchDayBook(filters, signal),
  });
  const report = query.data;

  const onExport = (format: ExportFormat) => {
    if (!report) return Promise.resolve();
    const table = dayBookTable(
      report,
      narrowDayBook(report, toolbar),
      columns.map((column) => column.key),
      { branchId: filters.branchId, branches: profile?.branches ?? [] },
    );
    return downloadReportExport(table, format);
  };

  return (
    <div>
      <PageHeader
        title="Day book"
        description="Money in and out of the shop, by day — only what actually moved, as on the till's Day Book."
      />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search type, reference, party or method"
        />
        <ReportFiltersButton
          fields={FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={(field) =>
            field === 'Direction' ? 'In / Out' : field === 'Method' ? 'Cash / Card' : field.toLowerCase()
          }
        />
        <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
        <ReportPeriodButton
          from={filters.from}
          to={filters.to}
          onChange={toolbar.setRange}
          deselected={toolbar.filterActive}
        />
        {report && report.days.length > 0 ? (
          <div className="ml-auto">
            <ExportMenu onExport={onExport} />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        isEmpty={(report) => report.days.length === 0}
        emptyTitle="No money moved in this period"
        skeleton={<TableSkeleton columns={10} />}
      >
        {(loaded) => {
          const { lines, days, totalIn, totalOut, net } = narrowDayBook(loaded, toolbar);
          return (
            <>
              <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <KpiCard label="Money in" value={money(totalIn)} />
                <KpiCard label="Money out" value={money(totalOut)} />
                <KpiCard label="Net" value={money(net)} />
              </div>
              <h2 className="mb-3 text-sm font-semibold text-slate-900">By day</h2>
              {days.length === 0 ? (
                <p className="mb-8 text-sm text-slate-500">No lines match this search.</p>
              ) : (
                <div className="mb-8 overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <DataTable
                    caption="Day book totals by day"
                    columns={DAY_COLUMNS}
                    rows={days}
                    rowKey={(row) => row.date}
                    totals={{
                      label: 'Total',
                      cells: {
                        count: String(lines.length),
                        totalIn: money(totalIn),
                        totalOut: money(totalOut),
                        net: money(net),
                      },
                    }}
                  />
                </div>
              )}
              <h2 className="mb-3 text-sm font-semibold text-slate-900">Every line</h2>
              {lines.length === 0 ? (
                <p className="text-sm text-slate-500">No lines match this search.</p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <DataTable
                    caption="Day book lines"
                    layout={layout}
                    columns={columns}
                    rows={lines}
                    rowKey={(row, index) => `${row.date}-${row.kind}-${row.reference}-${index}`}
                    totals={{ label: 'Total', cells: { moneyIn: money(totalIn), moneyOut: money(totalOut) } }}
                  />
                </div>
              )}
            </>
          );
        }}
      </AsyncSection>
    </div>
  );
}
